import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../contexts/AuthContext';
import { Flame, Trophy, TrendingUp, TrendingDown, AlertCircle, CheckCircle, Target, Users, ChevronDown } from 'lucide-react';

const ZONE_COLORS = {
  verte: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', dot: 'bg-emerald-500' },
  jaune: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', dot: 'bg-amber-500' },
  rouge: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', dot: 'bg-red-500' },
};

const CONSEIL_COLORS = {
  verte: 'text-emerald-400',
  jaune: 'text-amber-400',
  rouge: 'text-red-400',
};

export function NutritionBalance({ onRefreshNeeded }) {
  const [balance, setBalance] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showLb, setShowLb] = useState(true);

  const fetchData = useCallback(async () => {
    const results = await Promise.allSettled([
      apiClient.get('/nutrition/balance'),
      apiClient.get('/nutrition/leaderboard'),
    ]);
    if (results[0].status === 'fulfilled') {
      setBalance(results[0].value.data);
      setError(false);
    } else {
      setError(true);
    }
    if (results[1].status === 'fulfilled') setLeaderboard(results[1].value.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (onRefreshNeeded) {
      const interval = setInterval(fetchData, 15000);
      return () => clearInterval(interval);
    }
  }, [onRefreshNeeded, fetchData]);

  if (loading) {
    return (
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-6">
        <div className="flex justify-center">
          <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!balance) {
    return (
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-6 text-center" data-testid="balance-error">
        <AlertCircle className="w-6 h-6 text-[#c0392b] mx-auto mb-2" />
        <p className="text-xs text-zinc-400 mb-3">{error ? "Impossible de charger l'équilibre du jour" : 'Aucune donnée disponible'}</p>
        <button
          onClick={() => { setLoading(true); fetchData(); }}
          className="px-4 py-2 bg-[#FF6B35] text-white text-xs font-bold rounded-sm hover:bg-[#FF8C55]"
          data-testid="balance-retry-btn"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const zone = ZONE_COLORS[balance.zone] || ZONE_COLORS.rouge;
  const t = balance.targets;
  const c = balance.consumed;
  const hasMeals = balance.meal_count > 0;

  return (
    <div className="space-y-3" data-testid="nutrition-balance-section">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Target className="w-4 h-4 text-[#FF6B35]" />
        <h3 className="text-sm font-medium text-zinc-400">Equilibre du jour</h3>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${zone.bg} ${zone.text} font-bold`}>
          {balance.zone.toUpperCase()}
        </span>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-3 gap-2">
        <StatCard
          label="Mon rang"
          value={balance.rank ? `${balance.rank}e` : '—'}
          sub={balance.total_participants ? `/ ${balance.total_participants}` : ''}
          icon={Trophy}
          color="#FF6B35"
        />
        <StatCard
          label="Score"
          value={hasMeals ? balance.score : '—'}
          sub={hasMeals ? `/100` : ''}
          icon={Target}
          color={balance.zone === 'verte' ? '#22c55e' : balance.zone === 'jaune' ? '#eab308' : '#ef4444'}
        />
        <StatCard
          label="Pts Challenge"
          value={balance.challenge_points_total}
          sub={balance.streak > 0 ? `+${balance.streak}j` : ''}
          icon={Flame}
          color="#FFB800"
        />
      </div>

      {/* Gauge */}
      {hasMeals && <BalanceGauge ecart={balance.ecart_pct} meuteAvg={balance.meute_avg_ecart} zone={balance.zone} />}

      {/* Calorie Burn Breakdown */}
      <div className="bg-[#0F0F0F] rounded-sm p-3" data-testid="burn-breakdown">
        <div className="flex items-center gap-2 mb-2">
          <Flame className="w-3.5 h-3.5 text-[#FF6B35]" />
          <span className="text-[10px] text-zinc-500 font-semibold uppercase">Depense calorique du jour</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-[9px] text-zinc-600">Repos (BMR)</p>
            <p className="font-mono text-sm font-bold text-zinc-300">{t.repos || '—'}</p>
          </div>
          <div>
            <p className="text-[9px] text-zinc-600">Sport {balance.training_session ? `(${balance.training_session})` : ''}</p>
            <p className="font-mono text-sm font-bold text-[#FF6B35]">{balance.training_calories || 0}</p>
          </div>
          <div>
            <p className="text-[9px] text-zinc-600">Total brule</p>
            <p className="font-mono text-sm font-bold text-white">{t.tdee || '—'}</p>
          </div>
        </div>
        {t.kcal != null && t.tdee != null && t.kcal !== t.tdee && (
          <div className="mt-2 pt-2 border-t border-zinc-800/50 flex justify-between items-center" data-testid="objective-adjust-row">
            <span className="text-[10px] text-zinc-500">
              Ajustement objectif {({ cut: '(Perte de gras)', perte: '(Perte de poids)', bulk: '(Prise de masse)', recomp: '' })[balance.objective] || ''}
            </span>
            <span className="font-mono text-xs font-bold text-[#FFB800]">
              {t.kcal - t.tdee > 0 ? '+' : ''}{t.kcal - t.tdee} → cible {t.kcal} kcal
            </span>
          </div>
        )}
        <div className="mt-2 pt-2 border-t border-zinc-800/50 flex justify-between items-center">
          <span className="text-[10px] text-zinc-500">Bilan net</span>
          <span className={`font-mono text-sm font-bold ${(c.kcal - (t.tdee || 0)) < 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {c.kcal - (t.tdee || 0) > 0 ? '+' : ''}{c.kcal - (t.tdee || 0)} kcal
          </span>
        </div>
      </div>

      {/* Macro breakdown */}
      <div className="grid grid-cols-4 gap-1.5">
        <MacroBar label="Kcal" consumed={c.kcal} target={t.kcal} color="#FF6B35" />
        <MacroBar label="Prot" consumed={c.protein} target={t.protein} unit="g" color="#ef4444" />
        <MacroBar label="Gluc" consumed={c.carbs} target={t.carbs} unit="g" color="#eab308" />
        <MacroBar label="Lip" consumed={c.fat} target={t.fat} unit="g" color="#3b82f6" />
      </div>

      {/* Conseil */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-sm ${zone.bg} ${zone.border} border`}>
        {balance.conseil.level === 'verte' ? <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" /> :
         balance.conseil.level === 'jaune' ? <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" /> :
         <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />}
        <p className={`text-xs font-medium ${CONSEIL_COLORS[balance.conseil.level]}`}>
          Coach Namaste : {balance.conseil.text}
        </p>
      </div>

      {/* Leaderboard */}
      {leaderboard && leaderboard.leaderboard.length > 0 && (
        <div>
          <button onClick={() => setShowLb(!showLb)} className="flex items-center gap-2 w-full text-left mb-2" data-testid="lb-toggle">
            <Users className="w-4 h-4 text-[#FF6B35]" />
            <span className="text-sm font-medium text-zinc-400">Classement du jour</span>
            <span className="text-[10px] text-zinc-600">{leaderboard.total_participants} participants</span>
            <ChevronDown className={`w-3 h-3 text-zinc-500 ml-auto transition-transform ${showLb ? 'rotate-180' : ''}`} />
          </button>

          {showLb && (
            <div className="space-y-1">
              {leaderboard.leaderboard.map((entry) => (
                <div
                  key={entry.user_id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-sm text-xs ${
                    entry.is_me ? 'bg-[#FF6B35]/10 border border-[#FF6B35]/30' : 'bg-[#0F0F0F]'
                  }`}
                  data-testid={`lb-entry-${entry.user_id}`}
                >
                  {/* Rank */}
                  <span className={`w-6 text-center font-bold ${
                    entry.rank <= 3 ? 'text-[#FF6B35]' : 'text-zinc-600'
                  }`}>
                    {entry.rank <= 3 ? ['', '1', '2', '3'][entry.rank] : entry.rank}
                  </span>

                  {/* Avatar */}
                  <div className="w-7 h-7 rounded-full bg-[#2A2A2E] flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {entry.profile_photo ? (
                      <img src={entry.profile_photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[10px] text-zinc-400 font-bold">{entry.user_name[0]}</span>
                    )}
                  </div>

                  {/* Name + conseil */}
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium truncate ${entry.is_me ? 'text-white' : 'text-zinc-300'}`}>
                      {entry.user_name}
                    </p>
                    <p className={`text-[10px] ${CONSEIL_COLORS[entry.conseil.level]}`}>
                      {entry.conseil.text}
                    </p>
                  </div>

                  {/* Score */}
                  <div className="text-right flex-shrink-0">
                    <p className={`font-mono font-bold ${
                      entry.zone === 'verte' ? 'text-emerald-400' :
                      entry.zone === 'jaune' ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      {entry.score}
                    </p>
                    <p className="text-[9px] text-zinc-600">{entry.ecart_pct > 0 ? '+' : ''}{entry.ecart_pct}%</p>
                  </div>
                </div>
              ))}

              {/* Podium message */}
              {leaderboard.podium_message && (
                <div className="text-center py-2">
                  <p className="text-[10px] text-[#FF6B35] font-medium">{leaderboard.podium_message}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function StatCard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="bg-[#0F0F0F] rounded-sm p-2.5 text-center" style={{ borderTop: `2px solid ${color}` }}>
      <Icon className="w-3.5 h-3.5 mx-auto mb-1" style={{ color }} />
      <p className="font-mono text-lg font-bold text-white leading-none">{value}<span className="text-[10px] text-zinc-500 ml-0.5">{sub}</span></p>
      <p className="text-[9px] text-zinc-500 mt-0.5">{label}</p>
    </div>
  );
}


function BalanceGauge({ ecart, meuteAvg, zone }) {
  // Map ecart % to gauge position (0-100)
  // -30% to +30% mapped to 0-100
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const myPos = clamp(50 + ecart * (50 / 30), 2, 98);
  const avgPos = clamp(50 + meuteAvg * (50 / 30), 5, 95);

  return (
    <div className="bg-[#0F0F0F] rounded-sm p-3" data-testid="balance-gauge">
      <div className="flex justify-between text-[9px] text-zinc-600 mb-1">
        <span>-30%</span>
        <span className="text-emerald-500 font-bold">CIBLE</span>
        <span>+30%</span>
      </div>
      <div className="relative h-5 rounded-full overflow-hidden">
        {/* Zone bars */}
        <div className="absolute inset-0 flex">
          <div className="w-[13%] bg-red-500/20" />
          <div className="w-[12%] bg-amber-500/20" />
          <div className="w-[17%] bg-emerald-500/25" />
          <div className="w-[16%] bg-emerald-500/25" />
          <div className="w-[12%] bg-amber-500/20" />
          <div className="w-[13%] bg-red-500/20" />
        </div>
        {/* Center line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-zinc-600" />
        {/* Meute average marker */}
        <div className="absolute top-0 bottom-0 w-0.5 bg-zinc-500/50" style={{ left: `${avgPos}%` }} title="Moyenne meute">
          <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-2 h-2 bg-zinc-500 rounded-full" />
        </div>
        {/* My position marker */}
        <div className="absolute top-0 bottom-0" style={{ left: `${myPos}%` }}>
          <div className={`absolute -top-0.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 border-white ${
            zone === 'verte' ? 'bg-emerald-500' : zone === 'jaune' ? 'bg-amber-500' : 'bg-red-500'
          }`} />
        </div>
      </div>
      <div className="flex justify-between mt-1 text-[8px]">
        <span className="text-zinc-600 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-zinc-500 rounded-full inline-block" /> Meute</span>
        <span className={`font-bold ${zone === 'verte' ? 'text-emerald-400' : zone === 'jaune' ? 'text-amber-400' : 'text-red-400'}`}>
          {ecart > 0 ? '+' : ''}{ecart}% d'ecart
        </span>
        <span className="text-zinc-600 flex items-center gap-1"><span className={`w-1.5 h-1.5 rounded-full inline-block ${
          zone === 'verte' ? 'bg-emerald-500' : zone === 'jaune' ? 'bg-amber-500' : 'bg-red-500'
        }`} /> Moi</span>
      </div>
    </div>
  );
}


function MacroBar({ label, consumed, target, unit = '', color }) {
  const pct = target > 0 ? Math.min((consumed / target) * 100, 150) : 0;
  const isOver = consumed > target;

  return (
    <div className="bg-[#0F0F0F] rounded-sm p-2 text-center">
      <p className="text-[9px] text-zinc-500 mb-1">{label}</p>
      <div className="h-1.5 bg-[#2A2A2E] rounded-full overflow-hidden mb-1">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${Math.min(pct, 100)}%`,
            backgroundColor: isOver ? '#ef4444' : pct > 80 ? color : `${color}80`,
          }}
        />
      </div>
      <p className="font-mono text-[11px] font-bold text-white">
        {Math.round(consumed)}<span className="text-zinc-600">/{target}{unit}</span>
      </p>
    </div>
  );
}
